import React, { useState, useEffect } from "react";
import { X, Flag, CheckCircle2, AlertCircle } from "lucide-react";
import { createClientHito } from "../api";
import type { ChartHitoItem } from "./common/ChartTooltip";

interface RegisterHitoModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientSlug: string;
  onHitoCreated?: () => void;
  initialDomain?: string;
  initialHito?: ChartHitoItem | null;
}

export const RegisterHitoModal: React.FC<RegisterHitoModalProps> = ({
  isOpen,
  onClose,
  clientSlug,
  onHitoCreated,
  initialDomain = "SEO",
  initialHito = null,
}) => {
  const todaySantiago = new Date().toISOString().slice(0, 10);

  const [title, setTitle] = useState("");
  const [date, setDate] = useState(todaySantiago);
  const [domain, setDomain] = useState(initialDomain);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialHito) {
        setTitle(initialHito.title || "");
        setDate(initialHito.date || todaySantiago);
        setDomain(initialHito.category || initialDomain);
        setComment(initialHito.comment || "");
      } else {
        setTitle("");
        setDate(todaySantiago);
        setDomain(initialDomain);
        setComment("");
      }
      setError(null);
    }
  }, [isOpen, initialHito, initialDomain, todaySantiago]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("El título del hito es obligatorio.");
      return;
    }

    setSaving(true);
    setError(null);

    const idItem =
      initialHito?.id ||
      `act_${Date.now()}_${title.toLowerCase().replace(/[^a-z0-9]/g, "_").slice(0, 20)}`;
    const executedAtIso = `${date}T12:00:00-03:00`;

    try {
      await createClientHito({
        id_item: idItem,
        client_slug: clientSlug,
        dominio: domain,
        titulo: title.trim(),
        comentario: comment.trim() || undefined,
        executed_at: executedAtIso,
      });

      onHitoCreated?.();
      onClose();
    } catch (err: any) {
      console.error("Error al registrar hito:", err);
      setError(err?.message || "No fue posible registrar el hito en el sistema.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.7)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "var(--bg-surface, #1e2433)",
          border: "1px solid var(--border-color, rgba(255, 255, 255, 0.12))",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "480px",
          padding: "24px",
          color: "var(--text, #f8fafc)",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "18px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                background: "rgba(56, 189, 248, 0.15)",
                color: "#38bdf8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Flag size={18} />
            </span>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
              {initialHito ? "Editar Hito de Acción" : "Registrar Hito de Acción"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted, #94a3b8)",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={{ display: "block", fontSize: "12px", marginBottom: "5px", color: "var(--muted, #94a3b8)" }}>
              Cliente
            </label>
            <input
              type="text"
              value={clientSlug}
              disabled
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: "var(--muted, #94a3b8)",
                fontSize: "13px",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: "12px", marginBottom: "5px", fontWeight: 600 }}>
              Nombre de la acción / Hito <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Ej: Cambio sitio web, Optimización titles, Lanzamiento PMax"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                background: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.18)",
                color: "var(--text, #f8fafc)",
                fontSize: "13px",
              }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", marginBottom: "5px", fontWeight: 600 }}>
                Fecha de ejecución
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.18)",
                  color: "var(--text, #f8fafc)",
                  fontSize: "13px",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "12px", marginBottom: "5px", fontWeight: 600 }}>
                Dominio
              </label>
              <select
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.18)",
                  color: "var(--text, #f8fafc)",
                  fontSize: "13px",
                }}
              >
                <option value="SEO">SEO</option>
                <option value="Paid">Paid</option>
                <option value="Social">Social</option>
                <option value="Sitio Web">Sitio Web</option>
                <option value="General">General</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "12px", marginBottom: "5px", fontWeight: 600 }}>
              Detalle / Observaciones
            </label>
            <textarea
              placeholder="Describe los cambios realizados para correlacionar con los indicadores..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                background: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.18)",
                color: "var(--text, #f8fafc)",
                fontSize: "13px",
                resize: "vertical",
              }}
            />
          </div>

          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#f87171",
                fontSize: "12px",
              }}
            >
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "8px" }}>
            <button
              type="button"
              className="button button-ghost"
              onClick={onClose}
              disabled={saving}
              style={{ fontSize: "13px", padding: "8px 14px" }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="button"
              disabled={saving}
              style={{
                fontSize: "13px",
                padding: "8px 16px",
                background: "var(--accent, #3b82f6)",
                color: "#ffffff",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {saving ? "Guardando..." : initialHito ? "Actualizar Hito" : "Guardar Hito"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
